import { ChangeDetectionStrategy, Component, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { MessageService } from 'primeng/api';
import { combineLatest, Observable } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import {
  AdminState,
  CarrierProfileLoadAllAction,
  getAdminUsersLoading,
  getAllAuthorizedSecurityRoles,
  getAllAuthorizedShippers,
  getAllCarriers,
  getAllCarriersLoading,
  getSecurityRolesLoading,
  getSelectedUserCommunciation,
  getSelectedUserCommunciationLoading,
  getSelectedUserCommunciationUpdating,
  getShippersLoading,
  getUserCommunicationCreateMode,
  getUsers,
  UserAdminLoadAuthorizedEntitiesAction,
  UserAdminLoadUsersAction,
  UserCommunicationCreateAction,
  UserCommunicationLoadAction,
  UserCommunicationUpdateAction,
} from 'src/app/admin/store';
import { Carrier, Customer, ISecurityAccessRoleData, UserAdminData, UserCommunicationDetail } from 'src/app/shared/models';
import { BaseComponent } from '../../../../shared/components';

@Component({
  selector: 'kbxl-user-communication-form-container',
  templateUrl: './user-communication-form-container.component.html',
  styleUrls: ['./user-communication-form-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserCommunicationFormContainerComponent extends BaseComponent implements OnDestroy {
  userCommunication$: Observable<UserCommunicationDetail>;
  allCarriers$: Observable<Carrier[]>;
  allShippers$: Observable<Customer[]>;
  allSecurityRoles$: Observable<ISecurityAccessRoleData[]>;
  allUsers$: Observable<UserAdminData[]>;
  processing$: Observable<boolean>;
  createMode$: Observable<boolean>;
  routeInit = false;

  constructor(
    private adminStore: Store<AdminState>,
    private route: ActivatedRoute,
    private messageService: MessageService,
    private router: Router
  ) {
    super();
    const routeId$ = this.route.params.pipe(map((p) => p.id));
    this.createMode$ = this.adminStore.pipe(select(getUserCommunicationCreateMode));

    combineLatest([routeId$, this.createMode$])
      .pipe(
        takeUntil(this.destroyed$),
        map((args) => {
          const routeId = args[0];
          const createMode = args[1];
          return { routeId, createMode };
        })
      )
      .subscribe((routeData) => {
        if (!routeData.createMode && !this.routeInit) {
          if (!routeData.routeId) {
            this.messageService.add({ detail: 'Invalid User Communication Id provided.', severity: 'error' });
          } else if (routeData.routeId) {
            this.adminStore.dispatch(new UserCommunicationLoadAction({ userCommunicationId: routeData.routeId }));
          }
        }

        this.routeInit = true;
      });

    this.userCommunication$ = this.adminStore.pipe(
      select(getSelectedUserCommunciation),
      map((x) => this.deepClone(x))
    );

    this.adminStore.dispatch(new UserAdminLoadAuthorizedEntitiesAction());
    this.allShippers$ = this.adminStore.pipe(select(getAllAuthorizedShippers));
    this.allSecurityRoles$ = this.adminStore.pipe(select(getAllAuthorizedSecurityRoles));

    this.adminStore.dispatch(new CarrierProfileLoadAllAction());
    this.allCarriers$ = this.adminStore.pipe(select(getAllCarriers));

    this.adminStore.dispatch(new UserAdminLoadUsersAction({ query: '' }));
    this.allUsers$ = this.adminStore.pipe(select(getUsers));

    this.processing$ = combineLatest([
      this.adminStore.pipe(select(getSelectedUserCommunciationLoading)),
      this.adminStore.pipe(select(getAllCarriersLoading)),
      this.adminStore.pipe(select(getAdminUsersLoading)),
      this.adminStore.pipe(select(getSecurityRolesLoading)),
      this.adminStore.pipe(select(getShippersLoading)),
      this.adminStore.pipe(select(getSelectedUserCommunciationUpdating)),
    ]).pipe(map((args) => args[0] || args[1] || args[2] || args[3] || args[4] || args[5]));
  }

  userCommunicationUpdate(userCommunication: UserCommunicationDetail) {
    this.adminStore.dispatch(new UserCommunicationUpdateAction(userCommunication));
  }

  userCommunicationCreate(userCommunication: UserCommunicationDetail) {
    this.adminStore.dispatch(new UserCommunicationCreateAction(userCommunication));
  }

  userCommunicationCancel() {
    this.router.navigate(['maint/user-communications']);
  }
}
